import { Link, useLocation, useParams } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { getSeoLanding } from "@/data/seoLandings";

const LABELS: Record<string, string> = {
  "ai-agents": "AI Governance",
  solutions: "Solutions",
  spine: "Pharmalify Spine",
  eqms: "Pharmalify Spine",
  "regulatory-hub": "Regulatory Watch",
  platform: "Platform",
  about: "About",
  contact: "Contact",
  licensing: "Licensing & Validation",
  subscriptions: "Licensing & Validation",
  "security-compliance": "Trust & Validation",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  status: "System Status",
  changelog: "Changelog",
  auth: "Sign In",
};

interface Crumb { href: string; label: string; }

function buildCrumbs(pathname: string): Crumb[] {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return [];
  const crumbs: Crumb[] = [];
  let href = "";
  parts.forEach((seg, i) => {
    href += `/${seg}`;
    let label = LABELS[seg];
    // SEO landing leaf: /solutions/:slug
    if (!label && parts[0] === "solutions" && i === 1) {
      const data = getSeoLanding(seg);
      label = data?.h1 ?? data?.title ?? seg.replace(/-/g, " ");
    }
    if (!label) label = seg.replace(/-/g, " ");
    const crumbHref = parts[0] === "solutions" && i === 0 ? "/ai-agents" : href;
    crumbs.push({ href: crumbHref, label });
  });
  return crumbs;
}

const HIDE_ON = new Set(["/", "/auth", "/reset-password", "/payment-success"]);

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  if (HIDE_ON.has(pathname)) return null;
  if (pathname.startsWith("/dashboard") || pathname.startsWith("/admin") || pathname.startsWith("/my-workspace") || pathname.startsWith("/reviewer") || pathname.startsWith("/archive")) {
    return null;
  }
  const crumbs = buildCrumbs(pathname);
  if (crumbs.length === 0) return null;

  // JSON-LD BreadcrumbList for SEO
  const itemListElement = [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.pharmalify.com/" },
    ...crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 2,
      name: c.label,
      item: `https://www.pharmalify.com${c.href}`,
    })),
  ];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };

  return (
    <nav aria-label="Breadcrumb" className="border-b border-border/40 bg-background/60">
      <div className="container py-2.5">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li className="inline-flex items-center">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
              <Home className="h-3 w-3" />
              <span className="sr-only">Home</span>
            </Link>
          </li>
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <li key={c.href} className="inline-flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />
                {isLast ? (
                  <span aria-current="page" className="font-medium text-foreground capitalize">
                    {c.label}
                  </span>
                ) : (
                  <Link to={c.href} className="hover:text-foreground transition-colors capitalize">
                    {c.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </nav>
  );
};

export default Breadcrumbs;
